import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose, onlyUpdateForKeys } from 'recompose';
import styled, { StylesProps } from '../../lib/styles';
import Typography from '../../components/presentational/Typography/Typography';
import Spacer from '../../components/presentational/Spacer';
import Ad from '../../components/presentational/Ad';
import StickyRecirculation from './StickyRecirculation';
import ArticleSharing from './ArticleSharing';
import BlockUpdates from '../../behaviour/BlockUpdates';
import memoize from '../../lib/selectorUtils';
import {
  CATEGORY_CLICKED,
  CATEGORY_CLOUD_CLICKED,
  RECIRCULATED_STORY_CLICKED,
  SHARE,
} from './lib/ga';
import { trackEvent } from '../../lib/ga';

import standardArticleLayoutStyles from './StandardArticleLayout/StandardArticleLayout_styles';
import { suggestedArticlesSelector } from './store/selectors';
import { StandardArticleLayoutProps } from './StandardArticleLayout/StandardArticleLayout';
import AdhesionAd from './AdhesionAd';
import { GAME_VIEW, raiseEvent } from '../../lib/gtm';
import dayjs from 'dayjs';
import theme from "../../lib/theme";
const { layoutWidths } = theme;

const GameLayout = ({
  ads,
  suggestedArticles,
  hideStickyRecirc,
  hideAds = false,
  article: {
    title,
    link,
    categories,
    category,
    category_link,
    content,
    advertiserBuyoutExpiration,
    //@ts-ignore
    section = 'Discover',
  },
  styles,
}: StandardArticleLayoutProps &
  StylesProps<typeof standardArticleLayoutStyles>) => {
  const { targeting: defaultAdTargeting } = ads.standard_enterprise_billboard || {};
  const buyoutIsExpired = !advertiserBuyoutExpiration || dayjs(advertiserBuyoutExpiration).isBefore(dayjs(), 'day');

  useEffect(() => {
    console.log('GameLayout', { ads, advertiserBuyoutExpiration, defaultAdTargeting });
  }, []);

  useEffect(() => {
    raiseEvent({
      ...GAME_VIEW,
      ...{
        sasEventType: `GAME_VIEW: ${title}`,
      },
    });
  }, []);

  return (
    <Spacer space={'s'} width={layoutWidths.wideContentPane} align={'center'} className={`game-layout`}>
      <Spacer space={'s'} width="100%" align={'center'}>
        <Ad
          className={`adGameTop`}
          ad={ads.game_top}
          // ad={ads.standard_billboard}
          initialSize={[728, 90]}
          backgroundColor={'transparent'}
        />

        <Spacer className={'game-wrapper'} row width={'100%'} space={'m'} align={'stretch'}>

          <Spacer className={styles.lOnly} space={'m'} width={layoutWidths.sidebar} relative>
            <Ad
              className={`adGameSiderail1`}
              ad={ads.game_siderail1}
              align={'flex-start'}
              gutter={false}
              backgroundColor={'transparent'}
              renderMatch={'l'}
              initialSize={[300, 90]}
            />
          </Spacer>

          <Spacer className={`game-content ${styles.gameContent}`} flex={'auto'} width={'100%'} align={'center'} space={0}>
            <Spacer space={'s'}>
              <Spacer space={'s'}>
                <Spacer space={'xs'}>
                  <Spacer space={'xxxxs'}>
                    <Typography color={'primary'}>
                      <h5>
                        <a
                          href={category_link}
                          title={category}
                          className={styles.category}
                          onClick={() =>
                            trackEvent(
                              {
                                ...CATEGORY_CLICKED,
                                label: category,
                              },
                              { transport: 'beacon' }
                            )
                          }
                        >
                          <strong>{category}</strong>
                        </a>
                      </h5>
                    </Typography>

                    <Typography>
                      <h1>{title}</h1>
                    </Typography>
                  </Spacer>
                </Spacer>
                <div className={styles.bylineShares}></div>
              </Spacer>

              <Spacer space={'s'}>
                {process.env.COOKIE_NAME === 'id_token_dev' ? 
                  <img className={'mb-4'} src="https://placehold.co/300x150/f3f3f3/000?text=Game+Widget" />
                  :
                  <BlockUpdates
                    block
                    render={() => (
                      <Typography
                        size={'l'}
                        family={'copy'}
                        className={'game-typography d-flex justify-content-center flex-column align-items-center'}
                        dangerouslySetInnerHTML={{
                          __html: content.join('\n'),
                        }}
                      />
                    )}
                  />

                }

              </Spacer>
            </Spacer>

            <Spacer className={'cloudShares'} space={'s'} width={'100%'} align={'flex-start'}>
              <div className={styles.clouds}>
                {categories.map(({ name, link }) => (
                  <a
                    key={name}
                    href={link}
                    title={name}
                    target={'_blank'}
                    className={styles.cloud}
                    onClick={() =>
                      trackEvent(
                        { ...CATEGORY_CLOUD_CLICKED, label: name },
                        { transport: 'beacon' }
                      )
                    }
                  >
                    {name}
                  </a>
                ))}
              </div>

              <ArticleSharing
                link={link}
                brand="sas"
                onShare={network =>
                  trackEvent(
                    { ...SHARE, label: network },
                    { transport: 'beacon' }
                  )
                }
              />
            </Spacer>
          </Spacer>

          <Spacer className={styles.lOnly} space={'m'} width={layoutWidths.sidebar} relative>
            <Ad
              className={`adGameSiderail2`}
              ad={ads.game_siderail2}
              align={'flex-start'}
              gutter={false}
              backgroundColor={'transparent'}
              renderMatch={'l'}
              initialSize={[300, 90]}
            />
          </Spacer>
        </Spacer>
        <Spacer className={styles.gameIncontentWrapper} row space={'s'} width={'100%'} align={'center'} justify={'center'}>
          <Ad
            className={`adGameInContent1`}
            ad={ads.game_incontent1}
            align={'center'}
            backgroundColor={'transparent'}
            renderMatch={'l'}
            initialSize={[300, 250]}
          />
          <Ad
            className={`adGameInContent2`}
            ad={ads.game_incontent2}
            align={'center'}
            backgroundColor={'transparent'}
            renderMatch={'l'}
            initialSize={[300, 250]}
          />
        </Spacer>
      </Spacer>

      <Ad
        className={`adGameEnterpriseBillboard`}
        ad={ads.game_enterprise_billboard}
        align={'center'}
        backgroundColor={'transparent'}
        renderMatch={'l'}
        initialSize={[300, 250]}
      />
      
      {!hideAds && !hideStickyRecirc && (
        <StickyRecirculation
          recirculationAd1={ads.sticky_recirculation_native1}
          articles={suggestedArticles}
          collapsed={suggestedArticles.length < 1}
          onClick={(slug: string) =>
            trackEvent(
              {
                ...RECIRCULATED_STORY_CLICKED,
                label: `STICKY_${slug}`,
              },
              { transport: 'beacon' }
            )
          }
        />
      )}

      {buyoutIsExpired && (
        ads.game_adhesion && <AdhesionAd ad={ads.game_adhesion} />
      )}

      <script
        data-cfasync={'false'}
        dangerouslySetInnerHTML={{
          __html: `
          window._dmTrack("product", "${title.replace(/"/, "'")}");
          `,
        }}
      />
    </Spacer>
  );  
};

const standardArticleLayoutProps = memoize(
  suggestedArticlesSelector,
  suggestedArticles => ({
    suggestedArticles: suggestedArticles.slice(0, 4),
  })
);

export default compose(
  connect(standardArticleLayoutProps),
  styled(standardArticleLayoutStyles),
  onlyUpdateForKeys(['suggestedArticles'])
)(GameLayout);
